import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import {
  MessageBox,
  MessageBoxType
} from 'record-it-components/misc/MessageBox/MessageBox';
import {
  PageContentContainer,
  PageContentContainerProps
} from 'record-it-components/pageContentLayout/PageContentContainer/PageContentContainer';
import { PageContentHeader } from 'record-it-components/pageContentLayout/PageContentHeader/PageContentHeader';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { LatestEntityContainer } from '../../../classes/entityUtils/LatestEntityContainer/LatestEntityContainer';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { useEntityRepository } from '../../../hooks/entity/useEntityRepository';
import {
  CreateConstructionSiteForm,
  CreateConstructionSiteFormProps
} from '../CreateConstructionSiteForm/CreateConstructionSiteForm';

export type CreateConstructionSitePageContentProps =
  PropsWithoutChildren<PageContentContainerProps> & {
    userGroupId: string;
    onConstructionSiteCreated: (
      constructionSiteContainer: LatestEntityContainer<EntityName.ConstructionSite>
    ) => void;
    onCancel: () => void;
  };

export const CreateConstructionSitePageContent: FunctionComponent<CreateConstructionSitePageContentProps> =
  React.memo((props) => {
    const {
      userGroupId,
      onConstructionSiteCreated,
      onCancel,
      ...remainingProps
    } = prepareProps({ props, ignoreChildren: true });

    const constructionSiteRepository = useEntityRepository(
      EntityName.ConstructionSite
    );
    const compilations = useEntities(
      EntityName.TimeTrackingActivityCompilation,
      'getByUserGroupId',
      userGroupId
    );
    const defaultCompilation = compilations[0] ?? null;

    const { t } = useTranslation();

    const handleCreateConstructionSite: CreateConstructionSiteFormProps['onCreateConstructionSite'] =
      useEventCallback((constructionSiteToCreate) => {
        const constructionSite = constructionSiteRepository.create({
          ...constructionSiteToCreate,
          ownerUserGroupId: userGroupId
        });

        onConstructionSiteCreated(
          new LatestEntityContainer(
            constructionSite.id,
            constructionSiteRepository
          )
        );
      });

    return (
      <PageContentContainer {...remainingProps}>
        <PageContentHeader
          title={t(
            'components.constructionSite.CreateConstructionSitePageContent.title'
          )}
          iconConfig={
            iconConfigurations.prefixed.page.CreateConstructionSitePage
              .titleIcon
          }
        ></PageContentHeader>
        {defaultCompilation ? (
          <CreateConstructionSiteForm
            userGroupId={userGroupId}
            defaultTimeTrackingActivityCompilationId={defaultCompilation.id}
            onCreateConstructionSite={handleCreateConstructionSite}
            onCancel={onCancel}
            data-testid="CreateConstructionSitePageContent.Form"
          ></CreateConstructionSiteForm>
        ) : (
          <MessageBox
            type={MessageBoxType.Error}
            data-testid="CreateConstructionSitePageContent.NoCompilationMessageBox"
          >
            {t(
              'components.constructionSite.CreateConstructionSitePageContent.noCompilationFound'
            )}
          </MessageBox>
        )}
      </PageContentContainer>
    );
  });
